import React, { useState, useRef, useEffect } from 'react';
import {
  FiUpload,
  FiImage,
  FiFileText,
  FiSend,
  FiLoader,
  FiMessageCircle,
} from 'react-icons/fi';
import Sidebar from '../Sidebar';
import './PrepCopilot.css';
import Editor from '../Editor.js';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Copilot from './Copilot';
import './Copilot.css';
import {
  API_BASE_URL,
  API_SOCKET_URL,
  getAuthHeaders,
  redirectToLoginWithReturn,
  toastdisplay,
  checkTokenExpiration,
} from '../../utils/utils';
import {
  createAuthenticatedWebSocket,
  getStoredUser,
  hasAuthenticatedSession,
} from '../../utils/authSession';
import { renderMarkdownToSafeHtml } from '../../utils/safeHtml';

const PrepCopilot = () => {
  const [user, setUser] = useState(getStoredUser());
  const [context, setContext] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [messages, setMessages] = useState([
    { role: 'ai', content: 'I am analyzing your profile data...' },
  ]);
  const messagesEndRef = useRef(null);
  const [engine, setEngine] = useState(null);
  const [searchParams] = useSearchParams();
  const startProfile = searchParams.get('start') === 'profile';
  const showLoader = searchParams.get('loading') === 'true';
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [sessionExpired, setSessionExpired] = useState(false);
  const [dataContent, setDataContent] = useState('');
  const [dataTitle, setDataTitle] = useState(
    'Waiting for your profile generated by the Copilot...'
  );
  const [generating, setGenerating] = useState(true);
  const [copilotVisible, setCopilotVisible] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!hasAuthenticatedSession()) return;

    let socket;

    const connectSocket = () => {
      socket = createAuthenticatedWebSocket(`${API_SOCKET_URL}/api/ws/tasks`);
      if (!socket) return;

      socket.onopen = () => {
        console.log('WebSocket connected');
        setCopilotVisible(true);
      };

      socket.onmessage = (event) => {
        const update = event.data;

        setMessages((prev) => {
          // Avoid adding duplicate messages (especially repeated task descriptions)
          if (prev.length === 0 || prev[prev.length - 1].content !== update) {
            return [...prev, { role: 'ai', content: update }];
          }
          return prev;
        });
        setCopilotVisible(true);
      };

      socket.onerror = (e) => {
        console.error('WebSocket error:', e);
      };

      socket.onclose = () => {
        console.log('WebSocket disconnected');
      };
    };

    connectSocket();

    return () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    };
  }, []);

  function convertCustomTextToHTML(input) {
    return renderMarkdownToSafeHtml(input);
  }

  useEffect(() => {
    const runFillProfile = async () => {
      if (hasRunRef.current) return;
      hasRunRef.current = true;

      setGenerating(true);
      // Get the selected school
      const prioritySchool = localStorage.getItem('selectedPrioritySchool');
      if (!prioritySchool) {
        toastdisplay('No school selected for profile.', false, 2000, setToast);
        setGenerating(false);
        return;
      }

      try {
        const resp = await fetch(
          API_BASE_URL + '/api/generate_profile?userEmail=' + user.email,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (resp.status === 401) {
          redirectToLoginWithReturn({
            redirectTo: window.location.pathname + window.location.search,
            setToast,
          });
          setGenerating(false);
          return;
        }

        if (!resp.ok) {
          toastdisplay(
            'Something went wrong during profile processing.',
            false,
            2000,
            setToast
          );
        } else {
          const data = await resp.json();
          setDataContent(data.results.summary);
          setDataTitle(
            data.results.subject ||
              data.results.email?.subject ||
              'MBA Candidate Profile for evaluation'
          );
          setGenerating(false);
          if (data.results.email) {
            setEmailResult(data.results.email);
            setEmailSubject(data.results.email.subject || '');
          }
        }
      } catch (err) {
        console.error('Fill profile error:', err);
        toastdisplay(
          'Network error while submitting your profile.',
          false,
          2000,
          setToast
        );
        setGenerating(false);
      }
    };

    if (startProfile) runFillProfile();
  }, [startProfile]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileUpload = (e) => {
    const f = e.target.files[0];
    if (f) setSelectedFile(f);
  };

  const handleImageUpload = (e) => {
    const f = e.target.files[0];
    if (f) setSelectedImage(f);
  };

  const handleSend = async () => {
    if (!context.trim()) return;
    setMessages((m) => [...m, { role: 'user', content: context }]);
    setContext('');
    if (!engine) {
      setMessages((m) => [
        ...m,
        { role: 'ai', content: 'Loading model, please wait…' },
      ]);
      return;
    }
    const response = await engine.generate(context);
    setMessages((m) => [...m, { role: 'ai', content: response }]);
  };

  return (
    <div className="layout">
      <Sidebar />
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
        >
          <span className="toast-icon">{toast.success ? '✔️' : '❌'}</span>
          <span>{toast.message}</span>
        </div>
      )}
      <div className="content prepcopilot-content">
        <div className="prepcopilot-container">
          <div className="data-display">
            <div className="data-header">
              <h2>{dataTitle}</h2>
            </div>
            <div className="data-content">
              <div
                className="email-compose-card"
                style={{
                  background: 'var(--liwaza-surface-container, #f3edf7)',
                  borderRadius: 12,
                  boxShadow: '0 1px 4px rgba(38,39,48,0.08)',
                  padding: '2rem',
                  margin: '2rem 0',
                  maxWidth: 700,
                  marginLeft: 0,
                }}
              >
                <div style={{ marginBottom: 18 }}>
                  <label
                    htmlFor="email-subject"
                    style={{
                      fontWeight: 600,
                      color: 'var(--liwaza-primary, #262730)',
                      fontSize: 16,
                    }}
                  >
                    Subject
                  </label>
                  <input
                    id="email-subject"
                    type="text"
                    value={emailSubject}
                    onChange={(e) => {
                      setEmailSubject(e.target.value);
                      setEmailResult((er) =>
                        er ? { ...er, subject: e.target.value } : null
                      );
                    }}
                    style={{
                      width: '100%',
                      fontSize: 16,
                      padding: '10px 14px',
                      borderRadius: 8,
                      border:
                        '1.5px solid var(--liwaza-outline-variant, #e0e0e0)',
                      marginTop: 6,
                      marginBottom: 12,
                      background: 'var(--liwaza-surface-container, #f3edf7)',
                      color: 'var(--liwaza-on-surface, #212529)',
                    }}
                    placeholder="Email subject"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email-body"
                    style={{
                      fontWeight: 600,
                      color: 'var(--liwaza-primary, #262730)',
                      fontSize: 16,
                    }}
                  >
                    Body
                  </label>
                  <Editor
                    value={convertCustomTextToHTML(
                      emailResult ? emailResult.body : dataContent
                    )}
                    onChange={(val) =>
                      setEmailResult((er) => (er ? { ...er, body: val } : null))
                    }
                    id="email-body"
                  />
                </div>
              </div>
            </div>
          </div>
          {copilotVisible ? (
            <Copilot
              messages={messages}
              generating={generating}
              generatingText={'Generating'}
              context={context}
              setContext={setContext}
              handleSend={handleSend}
              handleFileUpload={handleFileUpload}
              handleImageUpload={handleImageUpload}
              selectedFile={selectedFile}
              selectedImage={selectedImage}
              messagesEndRef={messagesEndRef}
              onClose={() => setCopilotVisible(false)} // Add this
              showFileUpload={false}
              showImageUpload={false}
            />
          ) : (
            <div
              className="copilot-banner"
              onClick={() => setCopilotVisible(true)}
              style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                background: '#2D2F36',
                color: '#fff',
                padding: '10px 15px',
                borderRadius: '10px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 9999,
              }}
            >
              <FiMessageCircle style={{ marginRight: '8px' }} /> Get insights
              from Liwaza AI
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrepCopilot;
